import React, { useEffect, useState } from 'react';
import { UsersIcon, UserPlusIcon, CheckCircleIcon, XCircleIcon, ShieldIcon, UserIcon, EditIcon, SearchIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getAllUsers, getPendingUsers, updateUserRole, User } from '../../services/users';
import { UserRole } from '../../context/AuthContext';
const UserManagement: React.FC = () => {
  const {
    hasRole
  } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const [allUsers, pendingUsersList] = await Promise.all([getAllUsers(), getPendingUsers()]);
        setUsers(allUsers);
        setPendingUsers(pendingUsersList);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);
  const handleUpdateRole = async (userId: string, role: UserRole) => {
    try {
      await updateUserRole(userId, role);
      // Update local state
      setUsers(prevUsers => prevUsers.map(user => user.id === userId ? {
        ...user,
        role
      } : user));
      // If approving a pending user, remove from pending list
      if (role !== 'pending') {
        setPendingUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      }
      setSuccessMessage('User role updated successfully!');
      setEditingUser(null);
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Error updating user role:', err);
      setError('Failed to update user role. Please try again.');
    }
  };
  // Filter users by search term
  const filteredUsers = users.filter(user => user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase()));
  // Only superadmins can access this page
  if (!hasRole(['superadmin'])) {
    return <div className="text-center py-12" data-id="element-450">
        <ShieldIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-451" />
        <h2 className="text-2xl font-bold text-navy mb-2" data-id="element-452">Access Denied</h2>
        <p className="text-gray-600" data-id="element-453">
          You don't have permission to manage users.
        </p>
      </div>;
  }
  return <div data-id="element-454">
      <h1 className="text-2xl font-bold text-navy mb-6 font-montserrat" data-id="element-455">
        User Management
      </h1>
      {error && <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6" data-id="element-456">
          {error}
        </div>}
      {successMessage && <div className="bg-green-50 text-green-700 p-4 rounded-md mb-6" data-id="element-457">
          {successMessage}
        </div>}
      {/* Pending Users Section */}
      {pendingUsers.length > 0 && <div className="bg-yellow-50 rounded-lg shadow-sm p-6 mb-6" data-id="element-458">
          <h2 className="text-xl font-bold text-navy mb-4 font-montserrat flex items-center" data-id="element-459">
            <UserPlusIcon size={20} className="mr-2" data-id="element-460" />
            Pending Approval ({pendingUsers.length})
          </h2>
          <div className="overflow-x-auto" data-id="element-461">
            <table className="w-full" data-id="element-462">
              <thead className="bg-yellow-100" data-id="element-463">
                <tr data-id="element-464">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider" data-id="element-465">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider" data-id="element-466">
                    Email
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider" data-id="element-467">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-yellow-200" data-id="element-468">
                {pendingUsers.map(user => <tr key={user.id} className="hover:bg-yellow-100" data-id="element-469">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-470">
                      <div className="flex items-center" data-id="element-471">
                        {user.photoURL ? <img src={user.photoURL} alt={user.displayName} className="w-8 h-8 rounded-full mr-3" data-id="element-472" /> : <div className="w-8 h-8 rounded-full bg-navy text-white flex items-center justify-center mr-3" data-id="element-473">
                            {user.displayName.charAt(0).toUpperCase()}
                          </div>}
                        <div className="text-sm font-medium text-gray-900" data-id="element-474">
                          {user.displayName}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-475">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-476">
                      <div className="flex justify-end space-x-2" data-id="element-477">
                        <button onClick={() => handleUpdateRole(user.id, 'reader')} className="text-blue-600 hover:text-blue-900 bg-blue-100 p-1 rounded" title="Approve as Reader" data-id="element-478">
                          <UserIcon size={16} data-id="element-479" />
                        </button>
                        <button onClick={() => handleUpdateRole(user.id, 'contributor')} className="text-green-600 hover:text-green-900 bg-green-100 p-1 rounded" title="Approve as Contributor" data-id="element-480">
                          <CheckCircleIcon size={16} data-id="element-481" />
                        </button>
                        <button onClick={() => handleUpdateRole(user.id, 'rejected')} className="text-red-600 hover:text-red-900 bg-red-100 p-1 rounded" title="Reject" data-id="element-482">
                          <XCircleIcon size={16} data-id="element-483" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
      {/* All Users Section */}
      <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-484">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6" data-id="element-485">
          <h2 className="text-xl font-bold text-navy mb-4 md:mb-0 font-montserrat flex items-center" data-id="element-486">
            <UsersIcon size={20} className="mr-2" data-id="element-487" />
            All Users ({filteredUsers.length})
          </h2>
          <div className="relative w-full md:w-64" data-id="element-488">
            <input type="text" placeholder="Search users..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-489" />
            <SearchIcon size={18} className="absolute left-3 top-2.5 text-gray-400" data-id="element-490" />
          </div>
        </div>
        {loading ? <div className="flex justify-center py-12" data-id="element-491">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-navy" data-id="element-492"></div>
          </div> : filteredUsers.length === 0 ? <div className="text-center py-8" data-id="element-493">
            <UsersIcon size={36} className="mx-auto text-gray-400 mb-2" data-id="element-494" />
            <p className="text-gray-500" data-id="element-495">
              {searchTerm ? 'No users match your search.' : 'No users found.'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-496">
            <table className="w-full" data-id="element-497">
              <thead className="bg-gray-50" data-id="element-498">
                <tr data-id="element-499">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-500">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-501">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-502">
                    Role
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-503">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-504">
                {filteredUsers.map(user => <tr key={user.id} className="hover:bg-gray-50" data-id="element-505">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-506">
                      <div className="flex items-center" data-id="element-507">
                        {user.photoURL ? <img src={user.photoURL} alt={user.displayName} className="w-8 h-8 rounded-full mr-3" data-id="element-508" /> : <div className="w-8 h-8 rounded-full bg-navy text-white flex items-center justify-center mr-3" data-id="element-509">
                            {user.displayName.charAt(0).toUpperCase()}
                          </div>}
                        <div className="text-sm font-medium text-gray-900" data-id="element-510">
                          {user.displayName}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-511">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-512">
                      {editingUser?.id === user.id ? <select value={editingUser.role} onChange={e => setEditingUser({
                  ...editingUser,
                  role: e.target.value as UserRole
                })} className="border border-gray-300 rounded px-2 py-1 text-sm" data-id="element-513">
                          <option value="reader" data-id="element-514">Reader</option>
                          <option value="contributor" data-id="element-515">Contributor</option>
                          <option value="writer" data-id="element-516">Writer</option>
                          <option value="author" data-id="element-517">Author</option>
                          <option value="editor" data-id="element-518">Editor</option>
                          <option value="superadmin" data-id="element-519">Super Admin</option>
                        </select> : <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.role === 'superadmin' ? 'bg-purple-100 text-purple-800' : user.role === 'editor' ? 'bg-blue-100 text-blue-800' : user.role === 'author' ? 'bg-green-100 text-green-800' : user.role === 'writer' ? 'bg-teal-100 text-teal-800' : user.role === 'contributor' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-520">
                          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-521">
                      {editingUser?.id === user.id ? <div className="flex justify-end space-x-2" data-id="element-522">
                          <button onClick={() => handleUpdateRole(user.id, editingUser.role)} className="text-green-600 hover:text-green-900 bg-green-100 p-1 rounded" title="Save" data-id="element-523">
                            <CheckCircleIcon size={16} data-id="element-524" />
                          </button>
                          <button onClick={() => setEditingUser(null)} className="text-red-600 hover:text-red-900 bg-red-100 p-1 rounded" title="Cancel" data-id="element-525">
                            <XCircleIcon size={16} data-id="element-526" />
                          </button>
                        </div> : <button onClick={() => setEditingUser(user)} className="text-indigo-600 hover:text-indigo-900" disabled={user.role === 'superadmin' && filteredUsers.filter(u => u.role === 'superadmin').length === 1} title={user.role === 'superadmin' && filteredUsers.filter(u => u.role === 'superadmin').length === 1 ? 'Cannot edit the only Super Admin' : 'Edit Role'} data-id="element-527">
                          <EditIcon size={16} data-id="element-528" />
                        </button>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
};
export default UserManagement;